import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { Suspense, useEffect } from "react";
import {
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
  PreloadedQuery,
} from "react-relay";

import { useInitializeRound } from "@/screens/JoinBooking/ConfirmPanel/atoms/hooks/useInitializeRound";
import { SkeletonLoader } from "@whoosh/design-system/src/components/display/SkeletonLoader/SkeletonLoader";
import { HStack, VStack } from "@whoosh/design-system/src/components/primitive";

import { getRateOnRoundAtom } from "../../../../atoms/selectors/getRateOnRoundAtom/getRateOnRoundAtom";
import { JoiningUserRow } from "../JoiningUserRow/JoiningUserRow";
import { RateVerificationBanner } from "../RateVerificationBanner/RateVerificationBanner";
import { ConfigureRoundSessionQuery } from "./__generated__/ConfigureRoundSessionQuery.graphql";
import { ConfigureRound_Booking$key } from "./__generated__/ConfigureRound_Booking.graphql";
import { ConfigureRoundBundledRateAndTransportSelect } from "./components/ConfigureRoundBundledRateAndTransportSelect/ConfigureRoundBundledRateAndTransportSelect";
import { ConfigureRoundCourseLayoutControl } from "./components/ConfigureRoundCourseLayoutControl/ConfigureRoundCourseLayoutControl";
import { ConfigureRoundGolfPlayerRateControl } from "./components/ConfigureRoundPlayerRateControl/ConfigureRoundGolfPlayerRateControl";
import { ConfigureRoundTransportationTypeControl } from "./components/ConfigureRoundTransportationTypeControl/ConfigureRoundTransportationTypeControl";

type ConfigureRoundProps = {
  bookingRef: ConfigureRound_Booking$key | null;
};

export const ConfigureRound = ({ bookingRef }: ConfigureRoundProps) => {
  const [queryRef, loadQuery] = useQueryLoader<ConfigureRoundSessionQuery>(
    ConfigureRoundSessionQueryString
  );

  useEffect(() => {
    loadQuery({}, { fetchPolicy: "store-and-network" });
  }, [loadQuery]);

  return (
    <Suspense fallback={<SkeletonLoader height={52} width="100%" />}>
      {queryRef ? (
        <InnerConfigureRound bookingRef={bookingRef} sessionRef={queryRef} />
      ) : null}
    </Suspense>
  );
};

type InnerConfigureRoundProps = {
  bookingRef: ConfigureRound_Booking$key | null;
  sessionRef: PreloadedQuery<ConfigureRoundSessionQuery>;
};

export const InnerConfigureRound = ({
  bookingRef,
  sessionRef,
}: InnerConfigureRoundProps) => {
  const booking = useFragment(ConfigureRoundFragmentString, bookingRef);
  const data = usePreloadedQuery(ConfigureRoundSessionQueryString, sessionRef);
  const rate = useAtomValue(getRateOnRoundAtom);
  const currentClientProfileMember =
    data?.session?.currentClientProfile?.member;
  const currentMemberOrCustomer =
    currentClientProfileMember?.__typename === "Member" ||
    currentClientProfileMember?.__typename === "Customer"
      ? currentClientProfileMember
      : null;
  const currentMember =
    currentClientProfileMember?.__typename === "Member"
      ? currentClientProfileMember
      : null;

  const roundId = booking?.rounds?.edges?.find(
    (edge) => edge?.node?.player?.id === booking?.ownerClient?.id
  )?.node?.id;

  useInitializeRound({
    bookingRef: booking ?? null,
    roundId: roundId ?? "",
    playerId: currentMemberOrCustomer?.id ?? "",
  });

  const isPrivateClub =
    booking?.startSlot?.agenda.facility.club?.supportsPrivate ?? false;
  const shouldShowAsBundled =
    !isPrivateClub &&
    booking?.startSlot?.agenda.facility.publicBookingPermissionSet
      ?.rateDisplay === "BUNDLED";

  const membershipTypeId =
    currentClientProfileMember?.membershipType?.id ?? null;

  return (
    <VStack gap={4}>
      <JoiningUserRow
        firstName={currentMemberOrCustomer?.firstName ?? "Unknown"}
        lastName={currentMemberOrCustomer?.lastName ?? "User"}
      />
      <HStack gap={1}>
        <ConfigureRoundCourseLayoutControl bookingRef={booking ?? null} />
        {shouldShowAsBundled && membershipTypeId ? (
          <ConfigureRoundBundledRateAndTransportSelect
            membershipTypeId={membershipTypeId}
            currentClientId={currentMemberOrCustomer?.id ?? null}
            currentClientType={currentMember ? "MEMBER" : "CUSTOMER"}
            timeSlotId={booking?.startSlot?.id}
          />
        ) : (
          <>
            <ConfigureRoundTransportationTypeControl
              bookingRef={booking ?? null}
              withLabel={isPrivateClub}
            />
            {!isPrivateClub ? (
              <ConfigureRoundGolfPlayerRateControl
                bookingRef={booking ?? null}
              />
            ) : null}
          </>
        )}
      </HStack>
      <RateVerificationBanner
        rateId={rate?.id ?? ""}
        membershipTypeId={currentMember?.membershipType?.id ?? ""}
      />
    </VStack>
  );
};

const ConfigureRoundSessionQueryString = graphql`
  query ConfigureRoundSessionQuery {
    session {
      currentClientProfile {
        member {
          membershipType {
            id
          }
          __typename
          membershipType {
            id
          }
          ... on Member {
            id
            firstName
            lastName
          }
          ... on Customer {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

const ConfigureRoundFragmentString = graphql`
  fragment ConfigureRound_Booking on Booking {
    ...ConfigureRoundTransportationTypeControl_Booking
    ...ConfigureRoundCourseLayoutControl_Booking
    ...ConfigureRoundGolfPlayerRateControl_Booking
    ...useInitializeRound_Booking
    startSlot {
      id
      agenda {
        facility {
          club {
            supportsPrivate
          }
          publicBookingPermissionSet {
            rateDisplay
          }
        }
      }
    }
    ownerClient {
      ... on Member {
        id
      }
      ... on Customer {
        id
      }
    }
    rounds {
      edges {
        node {
          id
          player {
            id
          }
        }
      }
    }
  }
`;
